import { useState, useEffect, useMemo, useRef } from "react";
import { gaEvent } from "../lib/ga";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Sparkles,
  FileText, BarChart3, Banknote, Award, Building2,
  TrendingUp, Coins, Lock, Info, Briefcase,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import ClinqLogo from "../components/ClinqLogo";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { trackPageView } from "../lib/tracker";
import {
  REVENUE_BANDS, scoreFunderPreview, topFitGroups,
  CAPITAL_PURPOSES, CAPITAL_PURPOSE_LABEL,
} from "../lib/quizFunderPreview";
import { buildChecklist, checklistHasBuildableDocs } from "../lib/capitalDocChecklist";
import { DocInfoPopover } from "../components/founder/CapitalDocChecklist";
import { QuizResults, BUSINESS_COPY } from "../components/quiz/QuizResults";
import { computePreliminaryScore } from "../lib/quizPreliminaryScore";
import { ShareScore } from "../components/quiz/ShareScore";
import { ResetWelcomeBack } from "../components/quiz/ResetWelcomeBack";

const STORAGE_KEY = "clinq_get_started_v2";

const STAGES = [
  { id: "pre_revenue",  label: "Pre-revenue",  hint: "Developing and preparing for launch" },
  { id: "post_revenue", label: "Post-revenue", hint: "Actively generating income" },
];

// Industry the business operates in — same vocabulary as the funder quiz so
// founder ↔ funder sector matching lines up.
export const INDUSTRIES = [
  { id: "agriculture",   label: "Agriculture / AgriTech" },
  { id: "healthcare",    label: "Healthcare / HealthTech" },
  { id: "financial",     label: "Financial Services / FinTech" },
  { id: "manufacturing", label: "Manufacturing / Industrials" },
  { id: "retail",        label: "Retail / Consumer Goods" },
  { id: "logistics",     label: "Logistics & Supply Chain" },
  { id: "energy",        label: "Energy / CleanTech" },
  { id: "real_estate",   label: "Real Estate / Construction" },
  { id: "tech_saas",     label: "Technology / SaaS" },
  { id: "education",     label: "Education / EdTech" },
  { id: "hospitality",   label: "Hospitality / Tourism" },
  { id: "other",         label: "Other" },
];

const DOCS = [
  { id: "pitch_deck",         label: "Pitch deck or company profile",                    Icon: FileText,
    info: { what: "A short presentation (or one-pager) covering what your business does, your team, traction and how much you're raising.",
            why: "It's the first thing every funder opens — without it most won't take a meeting." } },
  { id: "mgmt_accounts",      label: "Management accounts",                              Icon: BarChart3,
    info: { what: "Your internal monthly or quarterly income statement, balance sheet and cash-flow. Unaudited Excel exports are fine.",
            why: "Funders use these first to judge your business's financial health." } },
  { id: "financial_model",    label: "Financial model",                                  Icon: Banknote,
    info: { what: "A spreadsheet projecting your revenue, costs and cash for the next 2–5 years, with your key assumptions.",
            why: "Funders test your assumptions here — it shows them how their money grows your business." } },
  { id: "audited_financials", label: "Audited Financials",                               Icon: Award,
    info: { what: "Annual financial statements reviewed and signed off by an independent, registered auditor.",
            why: "The strongest trust signal you can give — banks and institutional funders often require them." } },
  { id: "certificate",        label: "Certificate of incorporation / Company certificate", Icon: Building2,
    info: { what: "The official registration document proving your company legally exists (e.g. from the Registrar of Companies / BRS).",
            why: "Basic due diligence — no funder can disburse to an unregistered entity." } },
];

// Pre-revenue founders haven't traded yet, so historical / audit docs
// don't apply. Only Pitch deck, Financial model (projections) and
// Company certificate stay visible.
const PRE_REVENUE_HIDDEN_DOCS = new Set(["mgmt_accounts", "audited_financials"]);
const docsForStage = (stage) => (
  stage === "pre_revenue"
    ? DOCS.filter((d) => !PRE_REVENUE_HIDDEN_DOCS.has(d.id))
    : DOCS
);

// Canonical step numbers (1..7). Pre-revenue skips 2, 3 & 4 (revenue,
// profit, capital purpose) and jumps straight from Step 1 → 5 (funder preview).
const CANONICAL_STEPS = [1, 2, 3, 4, 5, 6, 7];

// Raise-amount brackets (KES) — asked right after the capital-purpose step.
const RAISE_BRACKETS = [
  { id: "under_1m",  label: "Under KES 1M" },
  { id: "1m_5m",     label: "KES 1M – 5M" },
  { id: "5m_15m",    label: "KES 5M – 15M" },
  { id: "15m_25m",   label: "KES 15M – 25M" },
  { id: "25m_50m",   label: "KES 25M – 50M" },
  { id: "50m_100m",  label: "KES 50M – 100M" },
  { id: "100m_500m", label: "KES 100M – 500M" },
  { id: "over_500m", label: "Over KES 500M" },
];

export default function GetStartedQuiz() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const requiredRedirect = params.get("required") === "1" && !!user && user.role === "founder";
  const [saveErr, setSaveErr] = useState("");
  const [step, setStep] = useState(1);
  // Virtual step views power the Admin quiz drop-off report.
  useEffect(() => { trackPageView(`/get-started/step-${step}`); }, [step]);
  const [stage, setStage] = useState("");
  const [industry, setIndustry] = useState("");                 // INDUSTRIES id
  const [industryOther, setIndustryOther] = useState("");       // free text when "other"
  const [showIndustryStep, setShowIndustryStep] = useState(false); // step-1b sub-view
  const [revenueBand, setRevenueBand] = useState("");         // e.g. "1m_5m"
  const [isProfitable, setIsProfitable] = useState(null);      // true | false | null
  const [purposes, setPurposes] = useState([]);                 // capital purposes (multi)
  const [primaryPurpose, setPrimaryPurpose] = useState("");     // one of `purposes`
  const [purposeOther, setPurposeOther] = useState("");         // free text when "other"
  const [raiseBracket, setRaiseBracket] = useState("");          // e.g. "5m_15m"
  const [showRaiseStep, setShowRaiseStep] = useState(false);     // step-4b sub-view
  const [docs, setDocs] = useState([]);
  const [showPrepNudge, setShowPrepNudge] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Funder-facing docs the Prep nudge cares about — unchanged from the
  // previous quiz. Post-revenue founders have all three on the table.
  const _RELEVANT_DOCS = stage === "post_revenue"
    ? ["pitch_deck", "financial_model", "mgmt_accounts"]
    : ["pitch_deck", "financial_model"];
  const _DOC_LABEL = {
    pitch_deck: "pitch deck",
    financial_model: "financial model",
    mgmt_accounts: "management accounts",
  };
  const _missingPrepDocs = _RELEVANT_DOCS.filter((id) => !docs.includes(id));
  const _missingLabels = _missingPrepDocs.map((id) => _DOC_LABEL[id]);
  const _missingPhrase = _missingLabels.length === 0 ? ""
    : _missingLabels.length === 1 ? _missingLabels[0]
    : _missingLabels.length === 2 ? `${_missingLabels[0]} or ${_missingLabels[1]}`
    : `${_missingLabels.slice(0, -1).join(", ")}, or ${_missingLabels.slice(-1)[0]}`;

  // Restore any prior draft so a back-tab doesn't lose progress.
  // `?fresh=1` (partner portals linking "who else can fund you") discards
  // any draft so the visitor starts at the pre/post-revenue question.
  useEffect(() => {
    try {
      if (params.get("fresh") === "1") {
        localStorage.removeItem(STORAGE_KEY);
        setHydrated(true);
        return;
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj?.stage)         setStage(obj.stage);
        if (obj?.industry)      setIndustry(obj.industry);
        if (typeof obj?.industryOther === "string") setIndustryOther(obj.industryOther);
        if (obj?.revenueBand)   setRevenueBand(obj.revenueBand);
        if (typeof obj?.isProfitable === "boolean" || obj?.isProfitable === "unsure") setIsProfitable(obj.isProfitable);
        if (Array.isArray(obj?.purposes)) setPurposes(obj.purposes);
        if (obj?.primaryPurpose) setPrimaryPurpose(obj.primaryPurpose);
        if (typeof obj?.purposeOther === "string") setPurposeOther(obj.purposeOther);
        if (obj?.raiseBracket) setRaiseBracket(obj.raiseBracket);
        if (Array.isArray(obj?.docs)) setDocs(obj.docs);
        // Resume at the step the founder left off (refresh-proof).
        const s = Number(obj?.step);
        if (Number.isInteger(s) && s >= 2 && s <= 7 && obj?.stage) setStep(s);
      }
    } catch (_) { /* ignore */ }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill from what Clinq already knows (revenue stage, uploaded docs) so
  // gated existing founders finish in seconds. Only fills blanks — never
  // overwrites answers restored from a local draft.
  useEffect(() => {
    if (!hydrated || !user || user.role !== "founder") return;
    if (stage && revenueBand && docs.length) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/companies/me/quiz-prefill");
        if (cancelled || !data) return;
        let used = false;
        if (!stage && data.stage) { setStage(data.stage); used = true; }
        if (!industry && data.industry) { setIndustry(data.industry); used = true; }
        if (!revenueBand && data.revenue_band) { setRevenueBand(data.revenue_band); used = true; }
        if (!docs.length && Array.isArray(data.docs) && data.docs.length) { setDocs(data.docs); used = true; }
        if (used) setPrefilled(true);
      } catch (_) { /* prefill is best-effort */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, user]);
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        stage, industry, industryOther, revenueBand, isProfitable, purposes, primaryPurpose, purposeOther, raiseBracket, docs, step,
      }));
    } catch (_) { /* ignore quota */ }
  }, [hydrated, stage, industry, industryOther, revenueBand, isProfitable, purposes, primaryPurpose, purposeOther, raiseBracket, docs, step]);

  // When the founder switches to pre-revenue, drop stale revenue + profit
  // answers so a back-and-forth doesn't leave invalid data in the preview.
  useEffect(() => {
    if (stage !== "pre_revenue") return;
    setRevenueBand("");
    setIsProfitable(null);
    setPurposes([]);
    setPrimaryPurpose("");
    setPurposeOther("");
    setDocs((curr) =>
      curr.some((id) => PRE_REVENUE_HIDDEN_DOCS.has(id))
        ? curr.filter((id) => !PRE_REVENUE_HIDDEN_DOCS.has(id))
        : curr
    );
  }, [stage]);

  const visibleDocs = docsForStage(stage);

  // Personalised Step-6 checklist: post-revenue founders who picked capital
  // purposes in Step 4 see the merged Required/Optional document list for
  // those purposes instead of the generic five.
  const personalizedDocs = stage === "post_revenue" && purposes.length > 0;
  const capChecklist = useMemo(() => buildChecklist(purposes), [purposes]);
  const capRequired = capChecklist.filter((i) => i.requirement === "required");
  const capOptional = capChecklist.filter((i) => i.requirement === "optional");

  // Which step numbers actually show up in the progress bar for this
  // founder's answers. Pre-revenue skips 2, 3 + 4.
  const visibleSteps = useMemo(() => (
    stage === "pre_revenue"
      ? CANONICAL_STEPS.filter((s) => s !== 2 && s !== 3 && s !== 4)
      : CANONICAL_STEPS.filter((s) => s <= 5)
  ), [stage]);
  const isResults = stage === "post_revenue" && step === 5;
  const preliminary = useMemo(() => (
    stage === "post_revenue"
      ? computePreliminaryScore({ revenueBand, raiseBracket, isProfitable, purposes, primaryPurpose })
      : null
  ), [stage, revenueBand, raiseBracket, isProfitable, purposes, primaryPurpose]);
  const shareAnswers = useMemo(() => ({ stage, revenueBand, isProfitable: String(isProfitable), purposes, primaryPurpose, raiseBracket }),
    [stage, revenueBand, isProfitable, purposes, primaryPurpose, raiseBracket]);
  const displayIdx  = visibleSteps.indexOf(step) + 1; // 1-based for copy
  const displayTotal = visibleSteps.length;

  // Compute funder preview once we hit Step 4. Memoised — same inputs =
  // same rows, so React doesn't re-render the cards on unrelated state
  // changes (checklist ticks etc.).
  const funderPreview = useMemo(() => {
    const band = REVENUE_BANDS.find((b) => b.id === revenueBand);
    const monthlyRevKES = band ? band.midKES : 0;
    return scoreFunderPreview({
      stage: stage || "pre_revenue",
      monthlyRevKES,
      isProfitable,
      purposes,
    });
  }, [stage, revenueBand, isProfitable, purposes]);
  const topPreview = useMemo(
    () => topFitGroups(funderPreview, purposes.length ? 5 : 4),
    [funderPreview, purposes.length]
  );

  // Provisional readiness teaser for Step 6 — derived from the same
  // funder-fit signals as Step 4, so signup feels earned, not blind.
  const provisional = useMemo(() => {
    const high = funderPreview.filter((r) => r.likelihood === "high").length;
    const med = funderPreview.filter((r) => r.likelihood === "medium").length;
    if (high >= 3) return { band: "Promising readiness", range: "est. 55–70 / 100" };
    if (high >= 1) return { band: "Developing readiness", range: "est. 40–55 / 100" };
    if (med >= 1) return { band: "Early readiness", range: "est. 30–45 / 100" };
    return { band: "Foundation stage", range: "est. 20–35 / 100" };
  }, [funderPreview]);
  const highFitLabels = useMemo(
    () => funderPreview.filter((r) => r.likelihood === "high").slice(0, 3).map((r) => r.label),
    [funderPreview]
  );

  const toggleDoc = (id) => {
    setDocs((curr) => curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id]);
  };

  const togglePurpose = (id) => {
    const next = purposes.includes(id) ? purposes.filter((x) => x !== id) : [...purposes, id];
    setPurposes(next);
    setPrimaryPurpose(next.includes(primaryPurpose) ? primaryPurpose : (next[0] || ""));
  };

  const purposeStepValid = purposes.length > 0
    && (!purposes.includes("other") || purposeOther.trim().length > 0);
  const industryStepValid = !!industry && (industry !== "other" || industryOther.trim().length > 0);

  // Move forward through the flow. Step 1 = stage, then industry (1b).
  // Pre-revenue jumps 1b → 5; post-revenue answers revenue, profitability
  // and capital purpose before the preview.
  const proceed = () => {
    if (step === 1) {
      if (!showIndustryStep) {
        if (!stage) return;
        setShowIndustryStep(true);
        return;
      }
      if (!industryStepValid) return;
      setShowIndustryStep(false);
      setStep(stage === "pre_revenue" ? 5 : 2);
      return;
    }
    if (step === 2) {
      if (!revenueBand) return;
      setStep(3);
      return;
    }
    if (step === 3) {
      if (isProfitable === null) return;
      setStep(4);
      return;
    }
    if (step === 4) {
      if (!showRaiseStep) {
        if (!purposeStepValid) return;
        setShowRaiseStep(true);
        return;
      }
      if (!raiseBracket) return;
      setShowRaiseStep(false);
      setStep(5);
      return;
    }
    if (step === 5) {
      if (stage === "post_revenue") { finishToSignup(); return; }
      setStep(6);
      return;
    }
    if (step === 6) {
      // Prep nudge ONLY when the founder has none of the funder-facing docs.
      // Ticking even one core doc means they're on their way — no scolding.
      if (_missingPrepDocs.length === _RELEVANT_DOCS.length) {
        setShowPrepNudge(true);
      } else {
        setStep(7);
      }
      return;
    }
  };
  const back = () => {
    setShowPrepNudge(false);
    if (step === 7) return setStep(6);
    if (step === 6) return setStep(5);
    if (step === 5) {
      // Pre-revenue came from the industry step; post-revenue came from the raise step.
      if (stage === "pre_revenue") { setShowIndustryStep(true); return setStep(1); }
      setShowRaiseStep(true);
      return setStep(4);
    }
    if (step === 4) {
      if (showRaiseStep) return setShowRaiseStep(false);
      return setStep(3);
    }
    if (step === 3) return setStep(2);
    if (step === 2) { setShowIndustryStep(true); return setStep(1); }
    if (step === 1 && showIndustryStep) return setShowIndustryStep(false);
  };

  // Browser Back steps backward through the quiz instead of leaving the
  // site. One sentinel history entry absorbs each Back press; on Step 1 we
  // stop re-pushing so the next Back exits normally.
  const stepRef = useRef(step);
  const backRef = useRef(back);
  useEffect(() => { stepRef.current = step; });
  useEffect(() => { backRef.current = back; });
  const industryViewRef = useRef(showIndustryStep);
  useEffect(() => { industryViewRef.current = showIndustryStep; });
  useEffect(() => {
    window.history.pushState({ quizStep: true }, "");
    const onPop = () => {
      if (stepRef.current > 1 || industryViewRef.current) {
        window.history.pushState({ quizStep: true }, "");
        backRef.current();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // CTA that hands the founder off to Prep so they can build the missing
  // doc(s). Unchanged from the previous flow.
  const launchPrep = async () => {
    if (user && user.role === "founder") {
      const params = new URLSearchParams({ redirect: "/new" });
      if (stage) params.set("prefill_stage", stage);
      if (_missingPrepDocs.length > 0) {
        params.set("prefill_missing_docs", JSON.stringify(_missingPrepDocs));
      }
      try {
        const { data } = await api.get("/companies/me");
        if (data?.company_name) params.set("prefill_company", data.company_name);
        if (data?.sector)       params.set("prefill_sector",  data.sector);
      } catch (_) { /* ignore — prefills are best-effort */ }
      const backend = process.env.REACT_APP_BACKEND_URL;
      window.location.href = `${backend}/api/sso/launch-prep?${params.toString()}`;
    } else {
      window.location.href = "https://prep.clinq.africa/?utm_source=clinq";
    }
  };
  const skipPrepNudge = () => {
    setShowPrepNudge(false);
    setStep(7);
  };

  // Final CTA. Pre-signup visitors: stash the draft and head to signup.
  // Logged-in founders (mandatory quiz gate): save answers server-side so the
  // gate flag flips, refresh /auth/me, then continue into the app — the NDA
  // gate is the next thing they'll see.
  const buildAnswersPayload = (completedAt) => ({
    stage: stage || "",
    industry: industry || "",
    industry_other: industry === "other" ? industryOther.trim() : "",
    revenue_band: revenueBand || "",
    is_profitable: isProfitable === true ? "yes"
      : isProfitable === false ? "no"
      : isProfitable === "unsure" ? "not_sure" : "",
    docs,
    capital_purposes: purposes,
    capital_purpose_primary: primaryPurpose || "",
    capital_purpose_other: purposeOther.trim(),
    raise_amount_bracket: raiseBracket || "",
    preliminary_score: preliminary?.score ?? null,
    completed_at: completedAt,
  });

  const finishToSignup = async () => {
    const completedAt = new Date().toISOString();
    gaEvent("quiz_complete", { quiz: "get_started" });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        stage, industry, industryOther, revenueBand, isProfitable, purposes, primaryPurpose, purposeOther, raiseBracket, docs,
        preliminary_score: preliminary?.score ?? null,
        completed_at: completedAt,
      }));
    } catch (_) { /* ignore */ }
    if (user && user.role === "founder") {
      try {
        await api.post("/companies/me/quiz-answers", buildAnswersPayload(completedAt));
      } catch (e) {
        setSaveErr("We couldn't save your answers — please check your connection and try again.");
        return;
      }
      await refresh();
      nav("/onboarding");
      return;
    }
    nav("/signup?role=founder");
  };

  // Results-page link: signed-out → sign-up; signed-in founder → pathway selection.
  const goToPathway = async () => {
    const completedAt = new Date().toISOString();
    gaEvent("quiz_pathway_link", { quiz: "get_started", signed_in: !!user });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        stage, industry, industryOther, revenueBand, isProfitable, purposes, primaryPurpose, purposeOther, raiseBracket, docs,
        preliminary_score: preliminary?.score ?? null, completed_at: completedAt,
      }));
    } catch (_) { /* ignore */ }
    if (user && user.role === "founder") {
      try { await api.post("/companies/me/quiz-answers", buildAnswersPayload(completedAt)); }
      catch (_) { setSaveErr("We couldn't save your answers — please check your connection and try again."); return; }
      try { await refresh(); } catch (_) { /* ignore */ }
      nav("/founder/pathway");
      return;
    }
    nav("/signup?role=founder");
  };

  const pathwayLink = (
    <p className="mt-6 text-center" data-testid="quiz-pathway-link-wrap">
      <button type="button" onClick={goToPathway} data-testid="quiz-pathway-link"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-300 underline decoration-emerald-300/40 underline-offset-4 transition-colors hover:text-emerald-200 hover:decoration-emerald-200">
        {user ? "Find out who can fund your business at the stage it's at today." : "Sign up to Clinq to find out who can fund your business at the stage it's at today."}
        <ArrowRight className="h-4 w-4" />
      </button>
    </p>
  );

  // Colour tokens by likelihood — same palette as the real report.
  const badgeClasses = (likelihood) => (
    likelihood === "high"
      ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-300"
      : likelihood === "medium"
        ? "border-amber-400/40 bg-amber-400/15 text-amber-300"
        : "border-white/15 bg-white/[0.04] text-white/50"
  );
  const badgeLabel = (l) => ({ high: "Possible Fit", medium: "Worth Exploring", low: "Long-shot" }[l]);

  return (
    <div className="min-h-screen bg-[#04302E] text-white" style={{ fontFamily: "Manrope, ui-sans-serif, system-ui, sans-serif" }}>
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="block" data-testid="quiz-logo-home"><ClinqLogo /></Link>
          <Link to="/login" data-testid="quiz-signin-link" className="text-sm font-semibold text-white/70 hover:text-white">
            Sign in
          </Link>
        </div>
      </nav>

      <div className="mx-auto max-w-3xl px-6 py-10 sm:py-16">
        {requiredRedirect && <ResetWelcomeBack noun="business" />}
        {requiredRedirect && (
          <div
            data-testid="quiz-required-banner"
            className="mb-8 rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-5 py-4 text-sm leading-relaxed text-amber-100"
          >
            To give you the best advice and move you toward Market Ready (a score of 70+),
            we need to know just a bit more about your business.
            {prefilled && (
              <span data-testid="quiz-prefill-note" className="mt-1.5 block text-xs text-amber-200/80">
                We&rsquo;ve pre-selected what Clinq already knows about your business — just confirm or adjust as you go.
              </span>
            )}
          </div>
        )}
        {/* Progress dots — hidden until the founder picks a revenue stage,
            then sized to their actual path (4 steps pre-revenue, 7 post). */}
        {stage ? (
          <div data-testid="quiz-progress">
            <div className="flex items-center justify-center gap-2">
              {visibleSteps.map((s) => (
                <span
                  key={s}
                  className={
                    "h-1.5 w-10 rounded-full transition-all " +
                    (s <= step ? "bg-[#F59E0B]" : "bg-white/15")
                  }
                />
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
              Step {displayIdx} of {displayTotal} · Capital Readiness Quiz
            </p>
          </div>
        ) : (
          <div className="text-center">
            <p data-testid="quiz-progress-pending" className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
              Capital Readiness Quiz
            </p>
            <p data-testid="quiz-value-statement" className="mx-auto mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm font-semibold text-emerald-200">
              Answer 5 quick questions and get your free business readiness score — plus the funder types most likely to back you.
            </p>
          </div>
        )}

        {/* ─── STEP 1 — stage ─── */}
        {step === 1 && !showIndustryStep && (
          <section data-testid="quiz-step-1" className="mt-10">
            <h1
              className="font-extrabold leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.875rem, 4.5vw, 3rem)" }}
            >
              What is the current stage of <span className="text-emerald-400">your business</span>?
            </h1>
            <p className="mt-4 text-base text-white/70">
              We&apos;ll use this to match you with the funders most likely to invest in companies like yours.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {STAGES.map((s) => {
                const active = stage === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`quiz-stage-${s.id}`}
                    onClick={() => setStage(s.id)}
                    className={
                      "group rounded-2xl border p-5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold tracking-tight">{s.label}</p>
                      {active && <CheckCircle2 className="h-5 w-5 text-[#F59E0B]" />}
                    </div>
                    <p className="mt-1 text-sm text-white/60">{s.hint}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-end">
              <button
                data-testid="quiz-step-1-next"
                disabled={!stage}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (stage
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 1b — industry (all founders) ─── */}
        {step === 1 && showIndustryStep && (
          <section data-testid="quiz-step-1b" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <Briefcase className="h-5 w-5 text-emerald-300" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              What <span className="text-emerald-400">industry</span> is your business in?
            </h1>
            <p className="mt-4 text-base text-white/70">
              Pick the closest match — funders tell us which sectors they back, so this is how we line you up with the right ones.
            </p>

            <div className="mt-8 grid gap-2.5 sm:grid-cols-2">
              {INDUSTRIES.map((ind) => {
                const active = industry === ind.id;
                return (
                  <button
                    key={ind.id}
                    type="button"
                    data-testid={`quiz-industry-${ind.id}`}
                    onClick={() => setIndustry(ind.id)}
                    className={
                      "flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className={
                      "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all " +
                      (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
                    }>
                      {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
                    </div>
                    <span className="text-sm font-semibold tracking-tight">{ind.label}</span>
                  </button>
                );
              })}
            </div>
            {industry === "other" && (
              <input
                data-testid="quiz-industry-other"
                value={industryOther}
                onChange={(e) => setIndustryOther(e.target.value)}
                maxLength={60}
                placeholder="Tell us your industry (e.g. Media, Fashion, Water)"
                className="mt-3 w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-emerald-400/60 focus:outline-none"
              />
            )}

            <div className="mt-8 flex items-center justify-between">
              <button data-testid="quiz-step-1b-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-1b-next"
                disabled={!industryStepValid}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (industryStepValid
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 2 — monthly revenue band (post-revenue only) ─── */}
        {step === 2 && (
          <section data-testid="quiz-step-2" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <TrendingUp className="h-5 w-5 text-emerald-300" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              How much does your business make on average <span className="text-emerald-400">per month</span>?
            </h1>
            <p className="mt-4 text-base text-white/70">Pick the band that matches your current sales / revenue.</p>
            <p className="mt-2 text-sm leading-relaxed text-white/60">
              Different funders back businesses at different revenue levels — a bank looks for steady cash flow it can lend against,
              while a VC hunts for scale-ready growth. Your monthly revenue tells us which funder categories are realistically in reach today.
            </p>

            <div className="mt-8 grid gap-2.5">
              {REVENUE_BANDS.map((b) => {
                const active = revenueBand === b.id;
                return (
                  <button
                    key={b.id}
                    type="button"
                    data-testid={`quiz-revenue-${b.id}`}
                    onClick={() => setRevenueBand(b.id)}
                    className={
                      "flex items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className={
                      "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all " +
                      (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
                    }>
                      {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
                    </div>
                    <Coins className={"h-4 w-4 flex-shrink-0 " + (active ? "text-[#F59E0B]" : "text-emerald-300/70")} />
                    <span className="text-sm font-semibold tracking-tight">{b.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button data-testid="quiz-step-2-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-2-next"
                disabled={!revenueBand}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (revenueBand
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 3 — profitability (post-revenue only) ─── */}
        {step === 3 && (
          <section data-testid="quiz-step-3" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <Coins className="h-5 w-5 text-emerald-300" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              Is your business <span className="text-emerald-400">profitable</span>?
            </h1>
            <p className="mt-4 text-base text-white/70">
              Debt providers (banks, MFIs) and PE funds heavily weight this — it changes which funders will consider you.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                { id: "yes",    label: "Yes",          val: true,     hint: "We make more than we spend right now." },
                { id: "no",     label: "No",           val: false,    hint: "Still investing — not yet turning a profit." },
                { id: "unsure", label: "I'm not sure", val: "unsure", hint: "No problem — we'll assume conservatively for now." },
              ].map((opt) => {
                const active = isProfitable === opt.val;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`quiz-profit-${opt.id}`}
                    onClick={() => setIsProfitable(opt.val)}
                    className={
                      "group rounded-2xl border p-5 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold tracking-tight">{opt.label}</p>
                      {active && <CheckCircle2 className="h-5 w-5 text-[#F59E0B]" />}
                    </div>
                    <p className="mt-1 text-sm text-white/60">{opt.hint}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="quiz-step-3-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-3-next"
                disabled={isProfitable === null}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (isProfitable !== null
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 4 — what is the capital for? (post-revenue only) ─── */}
        {step === 4 && !showRaiseStep && (
          <section data-testid="quiz-step-4" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <Coins className="h-5 w-5 text-emerald-300" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              What is the <span className="text-emerald-400">capital for</span>?
            </h1>
            <p className="mt-4 text-base text-white/70">
              Different needs are funded by completely different funder pools — a trade financier and a growth-equity fund won&apos;t look at the same deal. Select all that apply.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {CAPITAL_PURPOSES.map((opt) => {
                const active = purposes.includes(opt.id);
                const isPrimary = primaryPurpose === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`quiz-purpose-${opt.id}`}
                    onClick={() => togglePurpose(opt.id)}
                    className={
                      "group rounded-2xl border p-4 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-base font-bold tracking-tight">{opt.label}</p>
                      {active && <CheckCircle2 className="h-5 w-5 shrink-0 text-[#F59E0B]" />}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-white/60">{opt.hint}</p>
                    {active && purposes.length > 1 && (
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={`quiz-purpose-primary-${opt.id}`}
                        onClick={(e) => { e.stopPropagation(); setPrimaryPurpose(opt.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setPrimaryPurpose(opt.id); } }}
                        className={
                          "mt-2.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors " +
                          (isPrimary
                            ? "border-emerald-400/60 bg-emerald-400/20 text-emerald-300"
                            : "border-white/20 bg-white/[0.04] text-white/50 hover:border-emerald-400/50 hover:text-emerald-200")
                        }
                      >
                        {isPrimary ? "★ Primary need" : "Set as primary"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {purposes.includes("other") && (
              <div className="mt-4">
                <label className="text-xs font-bold uppercase tracking-wider text-white/60" htmlFor="quiz-purpose-other">
                  Tell us more — what is the capital for?
                </label>
                <textarea
                  id="quiz-purpose-other"
                  data-testid="quiz-purpose-other-text"
                  value={purposeOther}
                  onChange={(e) => setPurposeOther(e.target.value.slice(0, 500))}
                  rows={3}
                  placeholder="e.g. Buying out a business partner, meeting a regulator's minimum capital requirement, funding product R&D before launch…"
                  className="mt-2 w-full rounded-xl border border-white/15 bg-white/[0.04] p-3 text-sm text-white placeholder-white/30 focus:border-emerald-400/60 focus:outline-none"
                />
                <p className="mt-1 text-right text-[11px] text-white/40">{purposeOther.length}/500</p>
              </div>
            )}
            {purposes.length > 1 && (
              <p className="mt-4 text-xs text-white/50" data-testid="quiz-purpose-primary-hint">
                You picked more than one — tag your <b className="text-white/75">primary need</b> so we match funders in the right order.
              </p>
            )}

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="quiz-step-4-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-4-next"
                disabled={!purposeStepValid}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (purposeStepValid
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 4b — raise amount (post-revenue only) ─── */}
        {step === 4 && showRaiseStep && (
          <section data-testid="quiz-step-4b" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-400/15 ring-2 ring-emerald-400/30">
              <Coins className="h-5 w-5 text-emerald-300" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              How much are you <span className="text-emerald-400">looking to raise</span>?
            </h1>
            <p className="mt-4 text-base text-white/70">
              Funders have minimum and maximum ticket sizes — your raise amount decides which
              funder pools are a fit. A rough bracket is enough.
            </p>

            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {RAISE_BRACKETS.map((opt) => {
                const active = raiseBracket === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`quiz-raise-${opt.id}`}
                    onClick={() => setRaiseBracket(opt.id)}
                    className={
                      "group flex items-center justify-between rounded-2xl border p-4 text-left transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <p className="text-base font-bold tracking-tight">{opt.label}</p>
                    {active && <CheckCircle2 className="h-5 w-5 shrink-0 text-[#F59E0B]" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button data-testid="quiz-step-4b-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-4b-next"
                disabled={!raiseBracket}
                onClick={proceed}
                className={
                  "inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-all " +
                  (raiseBracket
                    ? "bg-[#0A8B85] text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
                    : "cursor-not-allowed bg-white/10 text-white/40")
                }
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 5 — results (shown to everyone; the card is the sign-up prompt) ─── */}
        {isResults && preliminary && (
          <QuizResults result={preliminary} funders={topPreview} badgeClasses={badgeClasses} badgeLabel={badgeLabel}
            onBack={back} onCta={goToPathway} saveErr={saveErr}
            copy={{ ...BUSINESS_COPY, cta: user ? BUSINESS_COPY.ctaSignedIn : BUSINESS_COPY.cta,
              reportTitle: user ? "Find out who can fund your business — at the stage it's at today." : BUSINESS_COPY.reportTitle }}
            shareSlot={<ShareScore quiz="business" score={preliminary.score} bandLabel={preliminary.band.label}
              answers={shareAnswers} />} />
        )}

        {step === 5 && !isResults && (
          <section data-testid="quiz-step-5" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F59E0B]/15 ring-2 ring-[#F59E0B]/30">
              <Sparkles className="h-5 w-5 text-[#F59E0B]" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              You&apos;re likely to get funded by <span className="text-emerald-400">these groups</span>
            </h1>
            <p className="mt-4 text-base text-white/70">
              Based on your answers so far. The full Capital Readiness Report ranks every funder in our network against your business.
            </p>
            {purposes.length > 0 && (
              <p className="mt-2 text-sm text-emerald-300/90" data-testid="quiz-preview-purpose-note">
                Weighted for your capital needs: <b>{purposes.map((p) => CAPITAL_PURPOSE_LABEL[p]).join(", ")}</b>
                {purposes.length > 1 && primaryPurpose && (
                  <> — with <b>{CAPITAL_PURPOSE_LABEL[primaryPurpose]}</b> as your primary need</>
                )}.
              </p>
            )}

            <div className="mt-8 grid gap-3">
              {topPreview.map((row) => (
                <div
                  key={row.id}
                  data-testid={`quiz-funder-${row.id}`}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold tracking-tight text-white">{row.label}</p>
                      <p className="mt-1 text-xs text-white/60">{row.blurb}</p>
                    </div>
                    <span
                      data-testid={`quiz-funder-${row.id}-badge`}
                      className={
                        "inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider " +
                        badgeClasses(row.likelihood)
                      }
                    >
                      {badgeLabel(row.likelihood)}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-white/50">{row.rationale}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4 text-xs text-white/60">
              <div className="flex items-center gap-2 text-white/70">
                <Lock className="h-3.5 w-3.5 text-emerald-300" />
                <span className="font-semibold">More matches locked</span>
              </div>
              <p className="mt-1">
                Upload your documents to unlock your <b className="text-white/85">full funder shortlist</b> — ranked, with
                specific funds and contacts in our network.
              </p>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button data-testid="quiz-step-5-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-5-next"
                onClick={proceed}
                className="inline-flex items-center gap-2 rounded-md bg-[#F59E0B] px-6 py-3 text-sm font-semibold tracking-tight text-[#04302E] shadow-lg shadow-black/30 ring-1 ring-amber-300/0 transition-all hover:bg-amber-400 hover:shadow-xl hover:ring-amber-200/60"
              >
                Get my full funder shortlist <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 6 — documents ─── */}
        {step === 6 && !showPrepNudge && (
          <section data-testid="quiz-step-6" className="mt-10">
            {personalizedDocs ? (<>
            <h1
              className="font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              Based on your capital needs, funders will ask for <span className="text-emerald-400">these documents</span>
            </h1>
            <p className="mt-4 text-base text-white/70">
              Tick the documents you currently have available.
            </p>
            <p className="mt-1.5 text-sm text-emerald-300/80" data-testid="quiz-docs-personalized-note">
              Tailored to: <b>{purposes.map((p) => CAPITAL_PURPOSE_LABEL[p]).join(", ")}</b>.
              You&apos;ll upload them after this quiz — for now, tick only what you truly have.
            </p>

            {[{ key: "required", title: "Required", items: capRequired },
              { key: "optional", title: "Optional — strengthens your case", items: capOptional }]
              .filter((g) => g.items.length > 0)
              .map((group) => (
              <div key={group.key} className="mt-7" data-testid={`quiz-docs-${group.key}`}>
                <p className={"text-[11px] font-bold uppercase tracking-[0.16em] " +
                  (group.key === "required" ? "text-[#F59E0B]" : "text-white/50")}>
                  {group.title}
                </p>
                <div className="mt-2.5 grid gap-2.5">
                  {group.items.map((d) => {
                    const active = docs.includes(d.id);
                    return (
                      <div
                        key={d.id}
                        className={
                          "flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all " +
                          (active
                            ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                            : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                        }
                      >
                        <button
                          type="button"
                          data-testid={`quiz-doc-${d.id}`}
                          onClick={() => toggleDoc(d.id)}
                          className="flex flex-1 items-center gap-3 text-left"
                        >
                          <div className={
                            "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all " +
                            (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
                          }>
                            {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
                          </div>
                          <FileText className={"h-4 w-4 flex-shrink-0 " + (active ? "text-[#F59E0B]" : "text-emerald-300/70")} />
                          <span className="flex-1 text-sm font-semibold tracking-tight">{d.label}</span>
                        </button>
                        <span className={
                          "inline-flex flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                          (d.requirement === "required"
                            ? "border-[#F59E0B]/50 bg-[#F59E0B]/15 text-[#F59E0B]"
                            : "border-white/20 bg-white/5 text-white/50")
                        } data-testid={`quiz-doc-${d.id}-badge`}>
                          {d.requirement === "required" ? "Required" : "Optional"}
                        </span>
                        <DocInfoPopover id={d.id} label={d.label} dark />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {checklistHasBuildableDocs(capChecklist) && (
              <div className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-400/10 p-4"
                data-testid="quiz-clinq-build-help">
                <p className="text-sm font-bold text-emerald-300">
                  Don&apos;t have a pitch deck or financial model yet? Clinq can help you build these.
                </p>
                <p className="mt-1 text-xs text-white/70">
                  Our advisory team helps founders put together investor-ready pitch decks and
                  financial models. Finish the quiz and ask David (bottom right) — or email
                  capital@clinq.africa.
                </p>
              </div>
            )}

            <p className="mt-6 text-xs text-white/50">
              Don&apos;t have some of these yet? That&apos;s fine — your report will show exactly what to put together first.
            </p>
            </>) : (<>
            <h1
              className="font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.5rem)" }}
            >
              To help us generate your <span className="text-emerald-400">Capital Readiness Report</span>, please select the documents you currently have available
            </h1>
            <p className="mt-4 text-base text-white/70">Tick all that apply.</p>
            <p className="mt-1.5 text-sm text-emerald-300/80" data-testid="quiz-docs-why">
              Each document unlocks a deeper, more accurate score — tick only what you truly have.
              Tap <Info className="inline h-3.5 w-3.5 -translate-y-px" aria-hidden="true" /> to see what each one means.
            </p>

            <div className="mt-8 grid gap-2.5">
              {visibleDocs.map((d) => {
                const active = docs.includes(d.id);
                const Icon = d.Icon;
                return (
                  <div
                    key={d.id}
                    className={
                      "flex items-center gap-3 rounded-xl border px-4 py-3.5 transition-all " +
                      (active
                        ? "border-[#F59E0B] bg-[#F59E0B]/10 ring-2 ring-[#F59E0B]/40"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/50 hover:bg-white/[0.06]")
                    }
                  >
                    <button
                      type="button"
                      data-testid={`quiz-doc-${d.id}`}
                      onClick={() => toggleDoc(d.id)}
                      className="flex flex-1 items-center gap-3 text-left"
                    >
                      <div className={
                        "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all " +
                        (active ? "border-[#F59E0B] bg-[#F59E0B]" : "border-white/30 bg-transparent")
                      }>
                        {active && <CheckCircle2 className="h-4 w-4 text-[#04302E]" />}
                      </div>
                      <Icon className={"h-4 w-4 flex-shrink-0 " + (active ? "text-[#F59E0B]" : "text-emerald-300/70")} />
                      <span className="text-sm font-semibold tracking-tight">{d.label}</span>
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid={`quiz-doc-info-${d.id}`}
                          aria-label={`What is ${d.label}?`}
                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white/40 transition-colors hover:bg-white/10 hover:text-emerald-300"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top" align="end" collisionPadding={12}
                        className="w-72 rounded-xl border border-emerald-400/25 bg-[#04302E] p-4 text-white shadow-2xl"
                        data-testid={`quiz-doc-popover-${d.id}`}
                      >
                        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">What it is</p>
                        <p className="mt-1 text-xs leading-relaxed text-white/85">{d.info.what}</p>
                        <p className="mt-3 text-[10px] font-bold uppercase tracking-widest text-[#F59E0B]">Why funders care</p>
                        <p className="mt-1 text-xs leading-relaxed text-white/85">{d.info.why}</p>
                      </PopoverContent>
                    </Popover>
                  </div>
                );
              })}
            </div>

            <p className="mt-6 text-xs text-white/50">
              Don&apos;t have any of these yet? That&apos;s fine — we&apos;ll show you exactly what to put together first.
            </p>
            </>)}

            <div className="mt-8 flex items-center justify-between">
              <button data-testid="quiz-step-6-back" onClick={back}
                className="inline-flex items-center gap-1 text-sm font-semibold text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                data-testid="quiz-step-6-next"
                onClick={proceed}
                className="inline-flex items-center gap-2 rounded-md bg-[#0A8B85] px-6 py-3 text-sm font-semibold tracking-tight text-white shadow-lg shadow-black/30 ring-1 ring-emerald-300/0 transition-all hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/20"
              >
                Next <ArrowRight className="h-4 w-4 text-emerald-300" />
              </button>
            </div>
          </section>
        )}

        {/* ─── STEP 6.5 — Prep nudge (unchanged from previous flow) ─── */}
        {step === 6 && showPrepNudge && (
          <section data-testid="quiz-prep-nudge" className="mt-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F59E0B]/15 ring-2 ring-[#F59E0B]/30">
              <Sparkles className="h-5 w-5 text-[#F59E0B]" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.5rem, 3.6vw, 2.25rem)" }}
            >
              Before you continue — most funders want to see a{" "}
              <span className="text-emerald-400">{_missingPhrase}</span>.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/75">
              These documents determine how many funders will take a serious look at your business.
              We recommend building these first — they will help unlock conversations with angels,
              VCs and other investors. Want our help putting them together?
            </p>
            <p className="mt-3 text-sm text-white/55">
              We&apos;ll open Clinq&apos;s Funder Readiness builder — it takes a few minutes. Or skip and we&apos;ll take you straight to your Capital Readiness Score.
            </p>

            {/* Debt path callout removed (Jun 2026) — debt & equity funders
                are treated as requiring the same documentation. */}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                data-testid="quiz-prep-nudge-yes"
                type="button"
                onClick={launchPrep}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#F59E0B] px-6 py-3 text-sm font-semibold tracking-tight text-[#04302E] shadow-lg shadow-black/30 ring-1 ring-amber-300/0 transition-all hover:bg-amber-400 hover:shadow-xl hover:ring-amber-200/60"
              >
                Yes, help me build {_missingLabels.length > 1 ? "them" : `a ${_missingLabels[0]}`}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                data-testid="quiz-prep-nudge-skip"
                type="button"
                onClick={skipPrepNudge}
                className="inline-flex items-center justify-center gap-1 rounded-md border border-white/25 px-5 py-3 text-sm font-semibold text-white transition-colors hover:border-emerald-300/60 hover:text-emerald-200"
              >
                No thanks — get my Capital Readiness score <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <button
              data-testid="quiz-prep-nudge-back"
              type="button"
              onClick={back}
              className="mt-10 inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to documents
            </button>
          </section>
        )}

        {/* ─── STEP 7 — final pitch ─── */}
        {step === 7 && (
          <section data-testid="quiz-step-7" className="mt-10 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#F59E0B]/15 ring-2 ring-[#F59E0B]/30">
              <Sparkles className="h-6 w-6 text-[#F59E0B]" />
            </div>
            <h1
              className="mt-5 font-extrabold leading-[1.1] tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
              style={{ fontFamily: "Manrope, sans-serif", fontSize: "clamp(1.875rem, 4.5vw, 3rem)" }}
            >
              You&apos;re one step away from your <span className="text-emerald-400">Capital Readiness Score</span>.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
              {user ? "Upload your documents" : "Create your free account, upload your documents"}, and get your Capital Readiness Score — plus, find out
              who is most likely to fund your business. Your files are{" "}
              <b className="text-white">100% secure and private</b>.
            </p>

            <div className="mx-auto mt-7 max-w-md rounded-2xl border border-emerald-400/25 bg-white/[0.04] p-5 text-left" data-testid="quiz-provisional-band">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">Your provisional result</p>
              <p className="mt-1.5 text-xl font-extrabold text-white">
                {provisional.band} <span className="text-sm font-semibold text-white/60">· {provisional.range}</span>
              </p>
              {highFitLabels.length > 0 ? (
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Possible fit already: <b className="text-white/85">{highFitLabels.join(", ")}</b>. Your full report pins the exact score and ranks every funder group against your business.
                </p>
              ) : (
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Your full report pins the exact score and shows the fastest path to your first funder matches.
                </p>
              )}
            </div>

            {saveErr && (
              <p data-testid="quiz-save-error" className="mt-6 rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">
                {saveErr}
              </p>
            )}

            <button
              data-testid="quiz-finish-cta"
              onClick={finishToSignup}
              className="group mt-8 inline-flex items-center gap-2 rounded-md bg-[#0A8B85] px-7 py-3.5 text-sm font-semibold tracking-tight text-white shadow-lg shadow-black/40 ring-1 ring-emerald-300/0 transition-all hover:bg-[#066667] hover:shadow-xl hover:ring-emerald-300/60 hover:shadow-emerald-400/30"
            >
              Get your Capital Readiness Score
              <ArrowRight className="h-4 w-4 text-emerald-300 transition-transform group-hover:translate-x-0.5" />
            </button>

            {pathwayLink}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> NDA-protected uploads
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Free to start
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> Built for African SMEs
              </span>
            </div>

            <button
              data-testid="quiz-step-7-back"
              onClick={back}
              className="mt-8 inline-flex items-center gap-1 text-xs font-semibold text-white/50 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to documents
            </button>
          </section>
        )}
      </div>
    </div>
  );
}
